import { Effect, Schema } from 'effect';
import { Bom } from './calculations';
import { Configuration, StockConfiguration } from './configurations';
import { CalculationStatus, Id, PositiveInteger, Price, Uuid } from './shared';

const PositiveNumber = Schema.Number.check(Schema.isGreaterThanOrEqualTo(1));

/**
 * The same rule as {@link PositiveInteger}, read off text the user typed.
 *
 * A quantity, a height and a width are the fields a position is counted and measured by, and the rule for what they may hold is the same whether the number comes from the API or an input field. Composed rather than restated, so there is one place to change it.
 */
export const PositiveIntegerFromString = Schema.FiniteFromString.pipe(Schema.decodeTo(PositiveInteger));

/**
 * The same rule as {@link PositiveNumber}, read off text the user typed.
 *
 * Where a position sits in the list is a number rather than a whole one: positions are put between each other, so 1.1 falls between 1 and 2. What it may not be is less than the first one.
 */
export const PositiveNumberFromString = Schema.FiniteFromString.pipe(Schema.decodeTo(PositiveNumber));
const NullOrNonEmptyString = Schema.NullOr(Schema.NonEmptyString);

/**
 * What a position costs altogether, as opposed to what one of it costs.
 *
 * Nothing until a calculation has been made, which is what the default is for: a position is built long before it has been priced, and nought is what it has cost so far.
 *
 * Effect.succeed and not Effect.sync, unlike {@link Id}: nought is the same nought every time, so there is nothing to work out again for each position.
 */
const TotalPrice = Price.pipe(Schema.withConstructorDefault(Effect.succeed(0)));

/**
 * The calculation the position's price was read off, or null when none has been.
 *
 * On {@link ModelPosition} and {@link StockPosition}, the two kinds the engine prices: both are configured and the configuration is what a calculation prices. A text position carries no price at all, and a field that could only ever be null on it says less than not having it.
 *
 * Its own field rather than the position's {@link Id}, and the two are not interchangeable. A position keeps one identity for as long as the user has it, which is what lets an answer that lands a minute late find the row it was asked for; a calculation id names one attempt at pricing it, and there is a new one every
 * time it is priced again.
 *
 * Null covers the two endings that have no calculation to name: a position nobody has priced yet, and one whose pricing never reached the engine. Both are said plainly rather than left pointing at some earlier calculation, which would claim the engine turned that one down when it did not.
 *
 * An absent field decodes to null too, so the day the API answers with positions of its own it need not carry one the Configurator is alone in keeping.
 */
const CalculationId = Schema.NullOr(Uuid).pipe(Schema.withDecodingDefaultKey(Effect.succeed(null)), Schema.withConstructorDefault(Effect.succeed(null)));

/**
 * The configuration the position is built from, or null when it has none.
 *
 * On {@link ModelPosition} alone, and for the same reason {@link CalculationId} is: a configuration says which model to build and how, and the other two kinds of position are not built at all.
 *
 * The configuration itself and not only its id, which is what separates it from {@link CalculationId}. A calculation is asked for, answered once and never read again, so an id is enough to name it by. A configuration is read every time the position is selected — its parameters are what the answers list shows — and
 * replaced by a new one whenever the user changes an answer. A position holding only an id would mean something else holding the configurations, keyed by that id, and answering which position each one belongs to. The position already answers that by holding it.
 *
 * It also means the two cannot drift apart: whatever configuration a position holds is the one its answers were last read from. The ones it held before are the API's to clear up — a changed answer is a new configuration rather than a changed one, so a position that has been answered ten times has left nine behind.
 *
 * Made before the position is priced. Null covers a position nobody has configured yet and one whose configuring failed — a model the store does not have is found out there, before a minute is spent pricing it.
 *
 * An absent field decodes to null too, so the day the API answers with positions of its own it need not carry one the Configurator is alone in keeping.
 */
const PositionConfiguration = Schema.NullOr(Configuration).pipe(Schema.withDecodingDefaultKey(Effect.succeed(null)), Schema.withConstructorDefault(Effect.succeed(null)));

/**
 * What the engine worked out about the position, or null when it has not worked it out.
 *
 * On {@link ModelPosition} and {@link StockPosition}, for the reason {@link CalculationId} gives: they are the kinds the engine prices.
 *
 * Kept rather than read off the calculation again, for the reason {@link PositionConfiguration} is kept: it is read every time the position is selected — the properties panel shows it — and a calculation is answered once and never asked for again. Asking the engine again on every selection would put a round trip behind
 * a held arrow key.
 *
 * Null covers the position nobody has priced yet, and nothing else: it is written beside {@link Position.price} and left beside it, so a position being priced again, and one the engine turned down, keep the measurements the price they still show was read off. The day one of the two is cleared, both are.
 *
 * An absent field decodes to null too, so the day the API answers with positions of its own it need not carry one the Configurator is alone in keeping.
 */
const PositionBom = Schema.NullOr(Bom).pipe(Schema.withDecodingDefaultKey(Effect.succeed(null)), Schema.withConstructorDefault(Effect.succeed(null)));

/**
 * What went wrong the last time the position was sent to the engine, or null when nothing did.
 *
 * On {@link ModelPosition} and {@link StockPosition}, for the reason {@link CalculationId} gives: they are the kinds that are sent anywhere. Two things write it. A calculation the engine gave up on says why in its own words — see `CalculationFailed` — and a configuration the API refused says so in the body of its
 * refusal, or, when the call never got that far, in a word of ours for what stopped it.
 *
 * Kept on the position rather than shown once and forgotten, because it is read wherever the position is: the geometry dialog shows it where the drawing would be, and the properties panel shows it under the properties, and the two must say the same thing. A position carries it until an answer goes through for it, and
 * that answer is a new position with none — so a refusal that follows a price leaves the price standing, with the refusal beside it.
 *
 * Null covers a position nothing has been refused for yet and one whose last answer went through. An empty text is refused rather than kept, since it would be a message that says nothing. An absent field decodes to null too, so the day the API answers with positions of its own it need not carry one the Configurator is
 * alone in keeping.
 */
const PositionError = NullOrNonEmptyString.pipe(Schema.withDecodingDefaultKey(Effect.succeed(null)), Schema.withConstructorDefault(Effect.succeed(null)));

class Position extends Schema.Class<Position>('Position')(
	Schema.Struct({
		description: NullOrNonEmptyString,
		id: Id,
		key: Schema.String,
		position: PositiveNumber,
		price: Price,
		quantity: PositiveInteger,
		status: CalculationStatus,
		totalPrice: TotalPrice,
	}),
) {}

export class ModelPosition extends Position.extend<ModelPosition>('ModelPosition')(
	Schema.Struct({
		bom: PositionBom,
		calculationId: CalculationId,
		configuration: PositionConfiguration,
		description: NullOrNonEmptyString,
		error: PositionError,
		height: PositiveInteger,
		key: Schema.String,
		litra: NullOrNonEmptyString,
		note: NullOrNonEmptyString,
		position: PositiveNumber,
		type: Schema.Literal('model'),
		width: PositiveInteger,
	}),
) {
	/**
	 * The configuration a change addresses, or null when the position has none.
	 *
	 * Derived rather than held beside the configuration, so nothing can be stored that disagrees with it — the same reason {@link ModelPosition.drawingId} is a getter.
	 */
	public get configurationId(): string | null {
		return this.configuration === null ? null : this.configuration.id;
	}

	/**
	 * The calculation whose drawing is on the CDN, or null when the position has none.
	 *
	 * The one place the rule lives, because two callers need it and they must not disagree: the engine writes an SVG when it prices a position, so a drawing exists exactly when a calculation succeeded — a position it turned down carries a calculation id and no drawing, and one still being worked out carries neither.
	 *
	 * A getter rather than a field, the way `CalculationSucceeded` reads its own price out of the BOM: nothing is stored that could disagree with the two values it is read from.
	 */
	public get drawingId(): string | null {
		return this.status === 'succeeded' ? this.calculationId : null;
	}
}

export class TextPosition extends Position.extend<TextPosition>('TextPosition')(
	Schema.Struct({
		text: Schema.NonEmptyString,
		type: Schema.Literal('text'),
	}),
) {}

/**
 * The stock configuration the position is priced from, or null when it has none.
 *
 * Held whole, for the reason {@link PositionConfiguration} is held whole on a model: its parameters are what the answers list shows, read every time the position is selected, and a changed answer is a new configuration rather than a changed one.
 *
 * It is also where the configuration the item comes in is named — `K001`, see `StockConfiguration.itemConfigurationKey` — rather than on the position beside it: one is picked after the position has been configured as its item, and the configuration is what the engine answers the pick with, so a field of the position's
 * own would be a second place to say it that could disagree.
 *
 * Null covers a position nobody has configured yet and one whose configuring failed — an item the store does not have, or a configuration it does not come in. An absent field decodes to null too, for the reason the model's does.
 */
const StockPositionConfiguration = Schema.NullOr(StockConfiguration).pipe(Schema.withDecodingDefaultKey(Effect.succeed(null)), Schema.withConstructorDefault(Effect.succeed(null)));

export class StockPosition extends Position.extend<StockPosition>('StockPosition')(
	Schema.Struct({
		bom: PositionBom,
		calculationId: CalculationId,
		configuration: StockPositionConfiguration,
		error: PositionError,
		type: Schema.Literal('stock'),
	}),
) {
	/** The configuration a change addresses, or null when the position has none. See {@link ModelPosition.configurationId}. */
	public get configurationId(): string | null {
		return this.configuration === null ? null : this.configuration.id;
	}
}

const Positions = Schema.Union([ModelPosition, TextPosition, StockPosition]);

export const PositionsArray = Schema.mutable(Schema.Array(Positions));
export type IPositions = typeof Positions.Type;
export type IPositionsArray = typeof PositionsArray.Type;
